import '/assets/styles/partials/qr-reader.scss';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

// Partials
import Btn from '/views/partials/btn';

// Context
import { useApplication } from '/utilities/application';
import { useModals } from '@wearetla/tla-essentials-tools/utilities/modals';

const readerId = 'qr-code-reader';

const QrReader = ({ config: rawConfig = {}, onSuccess, onError, className, paused }) => {
	const { isApplication, postMessage, cameraAllowed: appCameraAllowed } = useApplication();
	const [allowCamera, setAllowCamera] = useState(!isApplication);
	const [torchButtonAvailable, setTorchFeatureSupported] = useState(false);
	const [torchEnabled, setTorchEnabled] = useState(false);
	const blockTimeout = useRef(null);
	const torchFeatureRef = useRef(null);
	const { openModal } = useModals();

	const requestCameraPermission = () => {
		postMessage('requestCameraPermission');
	};

	const toggleTorch = () => {
		setTorchEnabled(!torchEnabled);
	}

	useEffect(() => {
		try {
			torchFeatureRef.current?.apply(torchEnabled);
		}
		catch(e) {
			console.log('Torch error:', e);
			setTorchEnabled(false);
		}
	}, [torchEnabled]);

	useEffect(() => {
		if (appCameraAllowed) {
			setAllowCamera(true);
		}
	}, [appCameraAllowed]);

	useEffect(() => {
		requestCameraPermission();

		return () => {
			try {
				if (window.qrReaderInstance) {
					window.qrReaderInstance
						?.stop?.()
						.then(() => {})
						.catch((error) => {
							console.error('Failed to clear Html5Qrcode. ', error);
						});
					window.qrReaderInstance = null;
				}
				if (blockTimeout.current) {
					clearTimeout(blockTimeout.current);
				}
				window.blockQrRead = false;
			} catch (e) {
				console.log('QR Reader error, ignore');
			}
		};
	}, []);

	useEffect(() => {
		if (!window.qrReaderInstance) {
			try {
				const { verbose, ...configOptions } = rawConfig;
				const config = {
					fps: 1,
					disableFlip: true,
					aspectRatio: 0.55,
					// qrbox: { width: 250, height: 400 },
					...configOptions,
				};

				if (!onSuccess) {
					throw 'onSuccess is required callback.';
				}

				window.qrReaderInstance = new Html5Qrcode(readerId, {
					verbose: !!verbose,
					formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
				});

				const successCallback = (decodedText, decodedResult) => {
					if (!window.blockQrRead) {
						window.blockQrRead = true;
						onSuccess?.(decodedText, decodedResult);

						blockTimeout.current = setTimeout(() => {
							window.blockQrRead = false;
						}, 2000);
					}
				};

				window.qrReaderInstance
					.start({ facingMode: 'environment' }, config, successCallback, onError).then(() => {
						const cameraCapabilities = window.qrReaderInstance.getRunningTrackCameraCapabilities();
						torchFeatureRef.current = cameraCapabilities?.torchFeature();
						setTorchFeatureSupported(torchFeatureRef.current?.isSupported());
					})
					.catch((e) => {
						openModal('message', { feedback: 'QR okuyucu başlatılamadı, lütfen tekrar deneyin.' });
						console.error('QR Error', e);
					});
			} catch (e) {
				openModal('message', { feedback: 'QR okuyucu başlatılamadı, lütfen tekrar deneyin.' });
				console.error('QR Error', e);
			}
		}
	}, [rawConfig, onSuccess, onError]);

	useEffect(() => {
		try {
			if (window.qrReaderInstance) {
				const instancePaused = window.qrReaderInstance.getState?.() === 3;
				if (paused && !instancePaused) {
					window.qrReaderInstance.pause?.();
				} else if (!paused && instancePaused) {
					window.qrReaderInstance.resume?.();
				}
			}
		} catch (e) {
			console.log('QR Reader error, ignore');
		}
	}, [paused]);

	return (
		<div className={classNames('qr-reader', className)}>
			<div className="qr-reader-video" id={readerId} />
			<div className="qr-reader-overlay">
				<div className="overlay-crosshair" />
				<div className="overlay-message">QR Kodu Okutun</div>
			</div>
			{!allowCamera && !appCameraAllowed && (
				<Btn onClick={requestCameraPermission} className="reader-camerabtn primary">
					Kamera İzni İste
				</Btn>
			)}
			{(allowCamera && torchButtonAvailable) &&
				<Btn
					onClick={toggleTorch}
					className={classNames('reader-torchbtn', 'big', { white: !torchEnabled, primary: torchEnabled })}
					icon={torchEnabled ? 'torch-on' : 'torch-off'} />
			}
		</div>
	);
};

export default QrReader;
